/**
 * columnsToGroups — turns a flat `Column[]` (as returned by the resolver's
 * `suggestColumnsInScope`) into table-grouped picker data so the VQE column
 * pickers can render "── payment ──" / "── staff (s) ──" sections with search,
 * data types, and PK/FK badges instead of one undifferentiated list.
 *
 * Pure + structured (no JSX) so it's trivially unit-testable; the consuming
 * `CommandPickerPopover` renders the badges/meta. Grouping is by source
 * relation identity (schema included — two same-named tables of different
 * schemas are two groups): the statement's own tables first, in the order the
 * statement reads them (then an enclosing query's), then any other table in
 * first-appearance order. The heading is what the query calls the table
 * (alias when aliased) — display only; each item carries the SQL the SELECT /
 * WHERE / ON edit emits separately (`sqlRef`).
 */

import type { QualifiedName } from '../../../../sql-grammar/model';
import { identifierKey, relationKey, sameRelation } from '../../../../sql-resolver/service/snapshotRelations';
import type { Column } from '../../../../sql-resolver/types/schema';
import { sourceRelation, type QuerySource } from '../../../../sql-resolver/scope/querySources';
import {
  columnSuggestionKey,
  columnTableLabel,
  formatColumnRef,
  sourceOfColumn,
  type ColumnRefs,
} from '../../edit/edits/selectItemHelpers';

export interface ColumnGroupItem {
  /** Stable id returned on select — `pickerKeysFor`: `relation.column` (folded). */
  readonly key: string;
  /** The bare column name as the user cased it. */
  readonly columnName: string;
  /** `tableLabel.columnName` — display text when the column needs to stand alone. Never SQL. */
  readonly qualifiedLabel: string;
  /** The dialect-correct SQL reference the emitters write (`"order items"."qty"`, `c.name`). */
  readonly sqlRef: string;
  /** Display text — the column's `formattedType` when the engine has one, else its raw `dataType`. */
  readonly dataType: string;
  readonly isPrimaryKey: boolean;
  readonly isForeignKey: boolean;
  /** Lower-case haystack for cmdk fuzzy search (table + column + type + key). */
  readonly searchValue: string;
  /** The resolved column, for callers that need `relation`/`qualified`. */
  readonly column: Column;
}

export interface ColumnGroup {
  /** Display heading — the table's alias or name (marked when from an enclosing query). */
  readonly heading: string;
  /**
   * Group key (`pickerKeysFor(…).groupKey`): the folded table name, or
   * `schema.table` when another schema's table in the same list shares the
   * name (`identifierKey` form either way). Keys the group and its "+ all" action.
   */
  readonly relationFolded: string;
  /**
   * The relation's identity (`relationKey`: `schema.table`, folded, whenever
   * it has a schema) — two same-named tables of different schemas are two
   * groups with their own scope.
   */
  readonly relationKey: string;
  /**
   * The relation's schema, set only when another schema's same-named table is
   * in the same list — shown next to the heading (`columnGroupTitle`) so two
   * `orders` groups can be told apart. Never part of the emitted qualifier.
   */
  readonly schema?: string;
  /** The query source reading this table; absent when the table is not in the query. */
  readonly source?: QuerySource;
  readonly items: readonly ColumnGroupItem[];
}

/** Display title of a group: its heading, plus the schema when the table name is shared (`o (sales)`). */
export function columnGroupTitle(group: Pick<ColumnGroup, 'heading' | 'schema'>): string {
  return group.schema ? `${group.heading} (${group.schema})` : group.heading;
}

export type ColumnPickerKeys = {
  /** Item key: `table.column` (folded, as `columnSuggestionKey`). */
  readonly columnKey: (column: Column) => string;
  /** Group key: `table` (folded). */
  readonly groupKey: (relation: QualifiedName) => string;
};

/**
 * Picker keys for one column list. A table whose name another schema's table
 * in the same list shares is keyed with its schema (`mia_s1.orders.status` vs
 * `mia_s2.orders.status`, groups `mia_s1.orders` / `mia_s2.orders`), so the
 * two never share a key, while every unambiguous key (and the `data-testid`
 * built from it) keeps its plain `table.column` / `table` form. Keys are
 * `identifierKey` paths: a dotted delimited name is quoted in them, so it
 * never collides with a schema-qualified or column key.
 */
export function pickerKeysFor(columns: readonly Column[]): ColumnPickerKeys {
  const relationsByName = new Map<string, Set<string>>();
  for (const c of columns) {
    let keys = relationsByName.get(c.relation.foldedName);
    if (!keys) {
      keys = new Set();
      relationsByName.set(c.relation.foldedName, keys);
    }
    keys.add(relationKey(c.relation));
  }
  const shared = (relation: QualifiedName): boolean =>
    (relationsByName.get(relation.foldedName)?.size ?? 0) > 1;
  return {
    columnKey: (column) =>
      shared(column.relation)
        ? `${relationKey(column.relation)}.${identifierKey(column.qualified.foldedName)}`
        : columnSuggestionKey(column),
    groupKey: (relation) => (shared(relation) ? relationKey(relation) : identifierKey(relation.foldedName)),
  };
}

type GroupBucket = {
  heading: string;
  relationFolded: string;
  schema?: string;
  source?: QuerySource;
  items: ColumnGroupItem[];
};

export function columnsToGroups(columns: readonly Column[], refs: ColumnRefs): ColumnGroup[] {
  const order: string[] = [];
  const byRelation = new Map<string, GroupBucket>();
  const keys = pickerKeysFor(columns);

  for (const column of columns) {
    const identity = relationKey(column.relation);
    const label = columnTableLabel(column, refs);
    let bucket = byRelation.get(identity);
    if (!bucket) {
      const source = sourceOfColumn(column, refs);
      const relationFolded = keys.groupKey(column.relation);
      bucket = {
        heading: source?.outer ? `${label} (outer query)` : label,
        relationFolded,
        ...(relationFolded !== identifierKey(column.relation.foldedName) && column.relation.schema
          ? { schema: column.relation.schema }
          : {}),
        source,
        items: [],
      };
      byRelation.set(identity, bucket);
      order.push(identity);
    }
    const key = keys.columnKey(column);
    const columnName = column.qualified.name;
    bucket.items.push({
      key,
      columnName,
      qualifiedLabel: `${label}.${columnName}`,
      sqlRef: formatColumnRef(column, refs),
      // PostgreSQL's raw `dataType` is `udt_name` (`_int4`, `varchar`); the
      // snapshot's `formattedType` carries `format_type` (`integer[]`,
      // `character varying(10)`) for every engine that has one — every other
      // engine's `dataType` already reads that way, so this falls through
      // unchanged there.
      dataType: column.formattedType ?? column.dataType,
      isPrimaryKey: column.isPrimaryKey,
      isForeignKey: column.isForeignKey,
      // `key` is folded + unique, so embedding it guarantees a unique cmdk value
      // even when a column name repeats across tables (e.g. store_id).
      searchValue: `${bucket.heading} ${columnName} ${column.dataType} ${key}`.toLowerCase(),
      column,
    });
  }

  const rank = (source: QuerySource | undefined) => {
    const at = source ? refs.sources.indexOf(source) : -1;
    return at < 0 ? Number.MAX_SAFE_INTEGER : at;
  };
  return order
    .map((identity) => {
      const bucket = byRelation.get(identity)!;
      return {
        heading: bucket.heading,
        relationFolded: bucket.relationFolded,
        relationKey: identity,
        ...(bucket.schema ? { schema: bucket.schema } : {}),
        ...(bucket.source ? { source: bucket.source } : {}),
        items: bucket.items,
      };
    })
    .sort((a, b) => rank(a.source) - rank(b.source));
}

/** What the picker knows about the statement it adds to. */
export type PickerScope = {
  /** False when the statement could not be read — nothing to narrow by. */
  readonly known: boolean;
  readonly sources: readonly QuerySource[];
  /** Snapshot keys of CTE names: an unqualified reference to one never means the base table. */
  readonly shadowKeys: ReadonlySet<string>;
};

export type InScopeGroups = {
  /** The tables the query reads (the picker's default list). */
  readonly visible: ColumnGroup[];
  /** Every table a pick may reference — `visible` plus the out-of-scope ones, minus CTE-shadowed names. */
  readonly all: ColumnGroup[];
  readonly hasOutOfScope: boolean;
  /** Sources whose columns are not known: CTEs, derived tables, table functions, tables missing from the snapshot. */
  readonly unresolved: readonly QuerySource[];
};

/** A group's relation (every item of a group shares it). */
function groupRelation(group: ColumnGroup): QualifiedName | undefined {
  return group.items[0]?.column.relation;
}

/**
 * Narrows grouped columns to the tables actually in the query's FROM/JOIN.
 * With no parse context, or a statement that reads no table at all, nothing is
 * narrowed. Otherwise the default list is exactly the tables the query reads —
 * possibly empty (a query over a CTE / derived table only): the picker then
 * says so instead of offering unrelated tables whose columns would emit SQL
 * the statement cannot run. A CTE shadows every same-named base table (any
 * schema's) for the out-of-scope list: its unqualified name would mean the CTE.
 */
export function selectInScopeGroups(groups: readonly ColumnGroup[], scope: PickerScope): InScopeGroups {
  if (!scope.known || scope.sources.length === 0) {
    return { visible: [...groups], all: [...groups], hasOutOfScope: false, unresolved: [] };
  }
  const visible = groups.filter((g) => g.source !== undefined);
  const all = groups.filter(
    (g) => g.source !== undefined || !scope.shadowKeys.has(groupRelation(g)?.foldedName ?? g.relationFolded),
  );
  // A source is listed when a visible group is its table (a self-join's second
  // instance shares the first one's group).
  const listed = (s: QuerySource): boolean => {
    const identity = sourceRelation(s);
    return (
      identity !== undefined &&
      visible.some((g) => {
        const relation = groupRelation(g);
        return relation !== undefined && sameRelation(identity, relation);
      })
    );
  };
  const unresolved = scope.sources.filter((s) => !s.outer && !listed(s));
  return { visible, all, hasOutOfScope: all.length > visible.length, unresolved };
}
